import { Link } from 'react-router-dom';
import { useDashboardKpis } from '../../api/kpis.js';
import type { Tenant } from '../../api/types.js';

interface Props {
  tenant: Tenant;
  // SCIs render with same visual weight as ops per
  // `feedback_sci_not_muted`; the only delta is the surrounding section
  // header. This prop is set true for the "0 invoices" empty-state copy
  // when the tenant is dormant and the count is zero.
  dormantFallback?: boolean;
}

// REQ-FE-029c — One card per société on the org-wide dashboard. Surfaces
// the four headline counts from `TenantKpis` and a CTA to the inbox.
// Mockup: `01-dashboard.png` ops + sci card grids.
export function SocieteCard({ tenant, dormantFallback = false }: Props) {
  const kpis = useDashboardKpis(tenant.tenantId);
  const aTraiter = kpis.data?.openAp.count ?? null;
  const urgentes = kpis.data?.overdue.count ?? null;
  const enLitige = kpis.data?.disputed?.count ?? null;
  const approuvees = kpis.data?.approvedLast30d?.count ?? null;
  const loading = kpis.isLoading;
  // SCI tenants with zero data: show explicit "0 invoices" copy rather
  // than the em-dash placeholder. Keeps the visual weight per feedback.
  const allZero =
    dormantFallback &&
    !loading &&
    (aTraiter ?? 0) === 0 &&
    (urgentes ?? 0) === 0 &&
    (enLitige ?? 0) === 0 &&
    (approuvees ?? 0) === 0;

  return (
    <article
      className="societe-card"
      data-testid={`societe-card-${tenant.tenantId}`}
      data-tenant-name={tenant.legalName}
    >
      <header className="societe-card__head">
        <h3 className="societe-card__name">{tenant.legalName}</h3>
        {tenant.siren ? (
          <div className="muted societe-card__siren">SIREN {tenant.siren}</div>
        ) : null}
      </header>
      {allZero ? (
        <div className="societe-card__zero" data-testid={`societe-card-${tenant.tenantId}-zero`}>
          0 invoices
        </div>
      ) : (
        <dl className="societe-card__counts">
          <Stat label="À traiter" value={aTraiter} loading={loading} />
          <Stat label="Urgentes" value={urgentes} loading={loading} tone="warning" />
          <Stat label="En litige" value={enLitige} loading={loading} tone="warning" />
          <Stat label="Approuvées" value={approuvees} loading={loading} />
        </dl>
      )}
      <Link
        to={`/t/${tenant.tenantId}/invoices`}
        className="societe-card__cta"
        data-testid={`societe-card-${tenant.tenantId}-cta`}
      >
        Voir les factures →
      </Link>
    </article>
  );
}

function Stat({
  label,
  value,
  loading,
  tone = 'neutral',
  placeholder = false,
}: {
  label: string;
  value: number | null;
  loading: boolean;
  tone?: 'neutral' | 'warning';
  placeholder?: boolean;
}) {
  return (
    <div className={`societe-card__stat societe-card__stat--${tone}`}>
      <dt className="eyebrow">{label}</dt>
      <dd className="societe-card__stat-value">
        {loading ? (
          <span className="muted">…</span>
        ) : placeholder || value === null ? (
          <span className="muted">—</span>
        ) : (
          value
        )}
      </dd>
    </div>
  );
}
